import { useRouter } from 'next/router'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import Cookies from 'js-cookie'
import { CookieNames } from 'server/cookies'

import { AllUKSProjects, useAllProjects } from 'hooks/useAllProjects'

interface ContextProps {
  selectedKey?: string
  loading: boolean
  setSelectedKey: (key?: string) => void
  projects: AllUKSProjects
  refreshData: () => void
}

const SelectedApiKeyContext = createContext<ContextProps>(null!)

const useSelectedApiKeyContext = () => {
  const context = useContext(SelectedApiKeyContext)

  if (!context) {
    throw new Error('useSelectedApiKeyContext must be used within a SelectedApiKeyContextProvider')
  }
  return context
}

export const useSelectedApiKey = () => {
  const { loading, projects, selectedKey } = useSelectedApiKeyContext()
  return { loading, projects, selectedKey }
}

export const useSetSelectedApiKey = () => {
  const { setSelectedKey } = useSelectedApiKeyContext()
  return setSelectedKey
}

export const useRefreshData = () => {
  const { refreshData } = useSelectedApiKeyContext()
  return refreshData
}

export const SelectedApiKeyContextProvider = ({ children }) => {
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined)
  const router = useRouter()
  const { isLoading, mutate, projects } = useAllProjects()

  const refreshData = useCallback(() => {
    const init = Cookies.get(CookieNames.SELECTED_API_KEY)
    if (init) {
      setSelectedKey(init)
    }
    mutate()
  }, [mutate, setSelectedKey])

  useEffect(() => {
    const init = Cookies.get(CookieNames.SELECTED_API_KEY)
    if (init) {
      setSelectedKey(init)
    }
  }, [])

  const handleKeyChange = (value?: string) => {
    if (typeof value !== 'string') {
      Cookies.remove(CookieNames.SELECTED_API_KEY)
      setSelectedKey(undefined)
    } else {
      Cookies.set(CookieNames.SELECTED_API_KEY, value, {
        expires: 86400,
        httpOnly: false,
        path: '/',
        sameSite: 'lax',
        secure: true
      })
      setSelectedKey(value)
    }

    // Refresh the page to trigger getServerSideProps on the active route
    router.replace(router.asPath)
  }

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (!selectedKey && projects) {
      const firstId = projects.ethereum[0].id

      if (firstId) {
        handleKeyChange(firstId)
      }
    }
  }, [isLoading, projects])

  return (
    <SelectedApiKeyContext.Provider
      value={{ loading: isLoading, projects, refreshData, selectedKey, setSelectedKey: handleKeyChange }}
    >
      {children}
    </SelectedApiKeyContext.Provider>
  )
}
